import React, { useEffect, useState } from "react";
import {
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { toast } from "react-toastify";
import moment from "moment";
import { useSelector } from "react-redux";
import CustomDialog from "../common/Dialog";
import TextField from "../common/TextField";
import Button from "../common/Button";
import ConfirmDialoag from "../common/ConfirmDialoag";
import CustomDatePicker from "../common/CustomDatePicker";
import AsyncSelect from "../common/asyncSelect";
import SingleSelect from "../common/SingleSelect";
import instance from "../../axios";
import {
  getExpenseSubTypes,
  getExpenseTypesByContract,
  markQuoteManualInvoiceCreated,
} from "./quotesApi";
import { isQuoteManualInvoiceCreated } from "./quoteUtils";

const emptyRow = () => ({
  expenseType: "",
  subTypes: "",
  expenseSubTypeId: null,
  quantity: "",
  price: "",
  description: "",
});

const ConvertToManualInvoiceModal = ({
  translate,
  themecolors,
  initialForm,
  onClose,
  onSuccess,
}) => {
  const logInUser = useSelector((state) => state.logInUsers);
  const ServerSetting = useSelector((state) => state.ServerSetting);
  const serverTimeZone = logInUser?.attributes?.timezone || "Asia/Riyadh";

  const [form, setForm] = useState(() => ({
    refNum: initialForm?.refNum || "",
    cn: initialForm?.cn || null,
    refDate: initialForm?.refDate || null,
    description: initialForm?.description || "",
    zerovat: Boolean(initialForm?.zerovat),
    attributes: {
      quantityPriceScale:
        initialForm?.attributes?.quantityPriceScale?.length
          ? initialForm.attributes.quantityPriceScale
          : [emptyRow()],
    },
  }));
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [deleteRowWarning, setDeleteRowWarning] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const [loader, setLoader] = useState(false);
  const [allExpenseTypes, setAllExpenseTypes] = useState([]);
  const [subTypeOptionsByType, setSubTypeOptionsByType] = useState({});
  const [defaultAccountsList, setDefaultAccountsList] = useState([]);

  const fetchAccountsList = async () => {
    try {
      const response = await instance.get(
        `/api/accounts/getlimiteddata?search=&all=true&userId=${logInUser.id}`,
      );
      const accounts =
        response?.map((acc) => ({
          ...acc,
          label: acc?.name || acc?.label,
          name: acc?.name || acc?.label,
          value: acc.id,
        })) || [];
      if (ServerSetting?.id) {
        accounts.unshift({
          id: ServerSetting.id,
          label: ServerSetting.name,
          name: ServerSetting.name,
          value: ServerSetting.id,
          contractNumber: ServerSetting.contractNumber,
        });
      }
      setDefaultAccountsList(accounts);
      return accounts;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const searchAccountsList = async (inputValue) => {
    if (!inputValue) return [];
    try {
      const response = await instance.get(
        `/api/accounts/getlimiteddata?search=${inputValue}&all=true&userId=${logInUser.id}`,
      );
      const result =
        response?.map((acc) => ({
          ...acc,
          label: acc?.name || acc.label,
          name: acc.name,
          value: acc.id,
        })) || [];
      setResultMessage(result.length === 0 ? "No option found" : "");
      return result;
    } catch (err) {
      return [];
    }
  };

  const hydrateExpenseData = async (contractNumber, scaleRows = []) => {
    // if (!contractNumber) return;
    try {
      const types = await getExpenseTypesByContract();
      setAllExpenseTypes(Array.isArray(types) ? types : []);

      const typeIds = [
        ...new Set(
          (scaleRows || [])
            .map((row) => row?.expenseType?.id || row?.expenseTypeId || null)
            .filter(Boolean)
            .map(String),
        ),
      ];

      const subMap = {};
      await Promise.all(
        typeIds.map(async (typeId) => {
          const rows = await getExpenseSubTypes(typeId);
          subMap[typeId] = rows || [];
        }),
      );
      setSubTypeOptionsByType(subMap);

      setForm((prev) => ({
        ...prev,
        attributes: {
          quantityPriceScale: (prev.attributes?.quantityPriceScale || []).map(
            (row) => {
              const typeId = row?.expenseType?.id || row?.expenseTypeId;
              const matchedType = (types || []).find(
                (t) => String(t.id) === String(typeId),
              );
              const subId = row?.subTypes?.id || row?.expenseSubTypeId || null;
              const matchedSub = (subMap[String(typeId)] || []).find(
                (s) => String(s.id) === String(subId),
              );
              return {
                ...row,
                expenseType: matchedType || row.expenseType || "",
                subTypes: matchedSub
                  ? {
                      ...matchedSub,
                      label: matchedSub.label || matchedSub.name || "",
                      name: matchedSub.name || matchedSub.label || "",
                    }
                  : row.subTypes || "",
                expenseSubTypeId: matchedSub?.id || row.expenseSubTypeId || null,
              };
            },
          ),
        },
      }));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchAccountsList().then((accounts) => {
      const selected = initialForm?.cn;
      if (!selected) return;
      const matched =
        (accounts || []).find(
          (acc) =>
            (selected.id && String(acc.id) === String(selected.id)) ||
            (selected.contractNumber &&
              acc.contractNumber === selected.contractNumber) ||
            (selected.name &&
              (acc.name === selected.name || acc.label === selected.name)),
        ) || selected;
      const nextCn = {
        ...matched,
        id: matched.id || selected.id,
        value: matched.id || matched.value || selected.id,
        label:
          matched.label ||
          matched.name ||
          selected.label ||
          selected.name ||
          "",
        name:
          matched.name ||
          matched.label ||
          selected.name ||
          selected.label ||
          "",
        contractNumber:
          matched.contractNumber || selected.contractNumber || "",
      };
      setForm((prev) => ({ ...prev, cn: nextCn }));
      if (nextCn.contractNumber) {
        hydrateExpenseData(
          nextCn.contractNumber,
          initialForm?.attributes?.quantityPriceScale,
        );
      }
    });
  }, []);

  useEffect(() => {
    const hasEmpty = form.attributes.quantityPriceScale.some(
      (row) => !row.expenseType || !row.quantity || !row.price,
    );
    setIsSubmitDisabled(
      !(form.refNum && form.refDate && form.cn && !hasEmpty),
    );
  }, [form]);

  const handleChange = (name, value) => {
    if (name === "cn") {
      setForm((prev) => ({ ...prev, cn: value }));
      if (value?.contractNumber) {
        hydrateExpenseData(value.contractNumber, []);
      } else {
        setAllExpenseTypes([]);
        setSubTypeOptionsByType({});
      }
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeDate = (name) => (date) => {
    const formatted = date
      ? moment(date).endOf("day").utc().format()
      : null;
    setForm((prev) => ({ ...prev, [name]: formatted }));
  };

  const recalcVat = (row) => {
    const quantity = Number(row.quantity) || 0;
    const price = Number(row.price) || 0;
    return quantity * price * 0.05;
  };

  const handleChangeScale = (field, value, index) => {
    const updated = [...form.attributes.quantityPriceScale];
    const numericFields = ["quantity", "price", "saleCost"];
    if (numericFields.includes(field) && value < 0) return;

    if (
      [
        "expenseType",
        "subTypes",
        "expenseSubTypeId",
        "description",
        "itemName",
        "itemCode",
        "category",
        "saleCost",
      ].includes(field)
    ) {
      if (field === "subTypes" && value) {
        updated[index].subTypes = value;
        updated[index].itemName = value.label || value.name || "";
        updated[index].itemCode = value.subTypeNum || "";
        updated[index].category = value.expenseType || "";
        updated[index].purchaseCost = parseFloat(value.purchasePrice) || 0;
        updated[index].price = parseFloat(value.unitPrice) || 0;
        updated[index].expenseSubTypeId = value.id || null;
        updated[index].quantity = updated[index].quantity || 1;
      } else {
        updated[index][field] = value;
      }
    } else {
      updated[index][field] = parseFloat(value) || 0;
    }

    const quantity = parseFloat(updated[index].quantity) || 0;
    const price = parseFloat(updated[index].price) || 0;
    const vat = form.zerovat
      ? 0
      : recalcVat({ ...updated[index], quantity, price });
    updated[index].vat = parseFloat(vat) || 0;
    updated[index].totalSingleItemCost = +(
      quantity * price +
      updated[index].vat
    ).toFixed(2);

    if (field === "expenseType") {
      const typeId = updated[index].expenseType?.id;
      if (typeId) {
        getExpenseSubTypes(typeId).then((rows) => {
          setSubTypeOptionsByType((prev) => ({
            ...prev,
            [String(typeId)]: rows || [],
          }));
        });
      }
      updated[index].subTypes = "";
      updated[index].expenseSubTypeId = null;
      updated[index].itemName = "";
      updated[index].itemCode = "";
      updated[index].vat = 0;
      updated[index].totalSingleItemCost = 0;
    }

    setForm((prev) => ({
      ...prev,
      attributes: { quantityPriceScale: updated },
    }));
  };

  const searchExpenseSubTypes = async (inputValue, expenseType) => {
    const typeId = expenseType?.id || expenseType;
    if (!typeId) return [];
    const rows = await getExpenseSubTypes(typeId, inputValue || "");
    setSubTypeOptionsByType((prev) => ({
      ...prev,
      [String(typeId)]: rows || [],
    }));
    setResultMessage(rows.length === 0 ? "No option found" : "");
    return rows;
  };

  const handleAddMore = () => {
    setForm((prev) => ({
      ...prev,
      attributes: {
        quantityPriceScale: [
          ...prev.attributes.quantityPriceScale,
          emptyRow(),
        ],
      },
    }));
  };

  const handleDelete = (index) => {
    if (form.attributes.quantityPriceScale.length === 1) {
      setDeleteRowWarning(true);
      return;
    }
    const updated = [...form.attributes.quantityPriceScale];
    updated.splice(index, 1);
    setForm((prev) => ({
      ...prev,
      attributes: { quantityPriceScale: updated },
    }));
  };

  const handleSubmit = async () => {
    const quoteId = initialForm?.quoteId || null;
    try {
      setLoader(true);
      if (quoteId) {
        // Re-check before create — only one manual invoice per quote
        const latest = await instance.get(`/api/quotes/${quoteId}`);
        const quoteBody =
          latest?.quote && typeof latest.quote === "object"
            ? latest.quote
            : latest;
        if (isQuoteManualInvoiceCreated(quoteBody)) {
          toast.error(translate("quoteManualInvoiceAlreadyCreated"));
          onClose?.();
          return;
        }
      }
      const payload = {
        manualInvoice: {
          contractNumber: form.cn?.contractNumber || "",
          refNum: form.refNum,
          refDate: form.refDate,
          description: form.description,
          purchaseTotal: 0,
          quantity: 0,
          saleTotal: 0,
          saleVat: 0,
          saleNet: 0,
          invoiceDate: form.refDate,
          attributes: {
            accountName: form.cn?.name || form.cn?.label || "",
            quoteId,
          },
        },
        manualInvoiceItems: form.attributes.quantityPriceScale.map((item) => ({
          itemName: item?.itemName || "",
          itemCode: item?.itemCode || "",
          expenseType: item.expenseType?.name,
          category: item.category || "",
          quantity: item.quantity,
          description: item.description,
          purchaseCost: item.purchaseCost,
          saleCost:
            item?.saleCost?.toFixed?.(2) ||
            item?.price?.toFixed?.(2) ||
            0,
        })),
      };
      await instance.post(
        `/api/manualinvoices?zerovat=${form.zerovat}`,
        payload,
      );
      if (quoteId) {
        try {
          await markQuoteManualInvoiceCreated(quoteId);
        } catch (markErr) {
          console.error(markErr);
          toast.error(translate("quoteManualInvoiceFlagFailed"));
        }
      }
      toast.success("Manual invoice saved successfully");
      onSuccess?.(quoteId);
      onClose?.();
    } catch (err) {
      toast.error("Failed to save");
      console.error(err);
    } finally {
      setLoader(false);
    }
  };

  return (
    <CustomDialog
      title={translate("convertToManualInvoice")}
      visable
      onClose={onClose}
      bodyPadding={10}
      hideDragIcon
      hideFooter
      fullWidth
      maxWidth="lg"
      themecolors={themecolors}
    >
      <Grid container spacing={1} style={{ padding: 8 }}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={translate("invoiceRefNumber")}
            fullWidth
            margin="dense"
            value={form.refNum}
            onChange={(e) => handleChange("refNum", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <CustomDatePicker
            format="DD/MM/YYYY"
            error={false}
            label={translate("date")}
            fullWidth
            value={
              form.refDate ? moment.tz(form.refDate, serverTimeZone) : null
            }
            onChange={handleChangeDate("refDate")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} style={{ zIndex: 99 }}>
          <AsyncSelect
            selectName="cn"
            isMulti={false}
            isClearable
            value={form.cn}
            placeholder={translate("searchAccount")}
            resultMessage={resultMessage}
            defaultOptions={defaultAccountsList}
            handleChange={handleChange}
            loadOptions={searchAccountsList}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Description"
            fullWidth
            margin="dense"
            value={form.description}
            onChange={(e) => handleChange("description", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.zerovat}
                onChange={(e) => {
                  const checked = e.target.checked;
                  const updated = form.attributes.quantityPriceScale.map(
                    (row) => {
                      const quantity = Number(row.quantity) || 0;
                      const price = Number(row.price) || 0;
                      const vat = checked ? 0 : quantity * price * 0.05;
                      return {
                        ...row,
                        vat,
                        totalSingleItemCost: +(
                          quantity * price +
                          vat
                        ).toFixed(2),
                      };
                    },
                  );
                  setForm((prev) => ({
                    ...prev,
                    zerovat: checked,
                    attributes: { quantityPriceScale: updated },
                  }));
                }}
              />
            }
            label="Zero VAT (Apply to all items)"
          />
        </Grid>
      </Grid>

      <Divider
        sx={{
          borderBottomWidth: 2,
          borderColor: "#ccc",
          borderStyle: "dashed",
          my: 1.5,
        }}
      />

      <Grid container>
        {form.attributes.quantityPriceScale.map((item, index) => (
          <Grid
            container
            spacing={1}
            key={index}
            sx={{
              paddingY: 1,
              borderBottom:
                index !== form.attributes.quantityPriceScale.length - 1
                  ? "1px dashed #ccc"
                  : "none",
            }}
          >
            <Grid item xs={12} md={3}>
              <TextField
                select
                label={translate("type")}
                value={item.expenseType || ""}
                onChange={(e) =>
                  handleChangeScale("expenseType", e.target.value, index)
                }
                margin="dense"
                fullWidth
                required
              >
                {allExpenseTypes.map((option) => (
                  <MenuItem key={option.id} value={option}>
                    {translate(option.name) || option.name || option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <SingleSelect
                id="expenseSubTypeId"
                array={
                  (item.expenseType?.id &&
                    subTypeOptionsByType[String(item.expenseType.id)]) ||
                  []
                }
                async
                isClearable
                selectName="subTypes"
                loadOptions={(inputValue) =>
                  searchExpenseSubTypes(inputValue, item.expenseType)
                }
                label={translate("subTypes")}
                value={
                  item.subTypes
                    ? {
                        id: item.subTypes.id,
                        value: item.subTypes.id,
                        label: item.subTypes.label || item.subTypes.name,
                      }
                    : ""
                }
                index={index}
                handleChange={handleChangeScale}
                canRemove
                canAssign
                menuPlacement="auto"
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Quantity"
                type="number"
                fullWidth
                margin="dense"
                value={item.quantity}
                onChange={(e) =>
                  handleChangeScale("quantity", e.target.value, index)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Price"
                type="number"
                fullWidth
                margin="dense"
                value={item.price}
                onChange={(e) =>
                  handleChangeScale("price", e.target.value, index)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={translate("description")}
                type="text"
                fullWidth
                margin="dense"
                value={item.description}
                onChange={(e) =>
                  handleChangeScale("description", e.target.value, index)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="vat"
                type="text"
                fullWidth
                margin="dense"
                disabled
                value={item.vat || ""}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="flex-start"
                mt={1}
              >
                <Chip
                  label={`Total: ${
                    item?.totalSingleItemCost?.toFixed?.(2) || "0.00"
                  }`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600, minWidth: 120 }}
                />
                <Tooltip title="Add another item">
                  <IconButton
                    size="small"
                    sx={{ bgcolor: "success.light" }}
                    onClick={handleAddMore}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove this item">
                  <IconButton
                    size="small"
                    sx={{ bgcolor: "error.main", color: "#fff" }}
                    onClick={() => handleDelete(index)}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        ))}
      </Grid>

      <div style={{ display: "flex", marginTop: 12 }}>
        <div style={{ marginLeft: "auto" }} />
        <Button size="small" style={{ marginRight: 15 }} onClick={onClose}>
          {translate("Cancel") || "Cancel"}
        </Button>
        <Button
          size="small"
          onClick={handleSubmit}
          disabled={isSubmitDisabled || loader}
        >
          Save
        </Button>
      </div>

      {deleteRowWarning && (
        <ConfirmDialoag
          onCancel={() => setDeleteRowWarning(false)}
          onOk={() => setDeleteRowWarning(false)}
          title="Cannot delete last row"
        />
      )}
    </CustomDialog>
  );
};

export default ConvertToManualInvoiceModal;
